import React, { useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, Truck, CheckCircle, Package, Clock, PaperPlaneTilt, Plus, Trash, Stack } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const inp = { width: "100%", padding: "9px 11px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 };
const card = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18 };
const th = { textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 700, padding: "6px 8px", textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "8px 8px", fontSize: 13, borderTop: "1px solid #F1F5F9" };

const Chip = ({ ok, children }) => (
  <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999,
    background: ok ? "#DCFCE7" : "#FEE2E2", border: `1px solid ${ok ? "#BBF7D0" : "#FECACA"}`,
    color: ok ? "#166534" : "#991B1B", whiteSpace: "nowrap" }}>{children}</span>
);

const poll = async (jobId, onStep) => {
  let last = null;
  for (let i = 0; i < 360; i++) {        // ~15 min safety ceiling (jobs normally finish in 1–5 min)
    await new Promise((r) => setTimeout(r, 2500));
    try {
      const { data } = await api.get(`/sap/dispatch-test/combine/job/${jobId}`, { timeout: 30000 });
      last = data;
      if (onStep) onStep(data);
      if (data.status === "done" || data.status === "error") return data;
    } catch (_) { /* transient blip — keep polling, the job is still alive server-side */ }
  }
  // Client stopped waiting, but the JOB IS STILL ALIVE on the server. NEVER report this as a failure —
  // the goods may already be issuing. Surface it as "still running" so the operator does not re-dispatch.
  return { ...(last || {}), status: "running", _clientTimedOut: true, ok: false, job_id: jobId };
};

export default function DispatchCombine() {
  const [soInput, setSoInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [sos, setSos] = useState([]);            // [{so_id, lines:[...]}]
  const [dispatchId, setDispatchId] = useState("");
  const [busy, setBusy] = useState(false);
  const [liveStep, setLiveStep] = useState("");
  const [result, setResult] = useState(null);
  const [stepBusy, setStepBusy] = useState("");     // which step is running: prepare|gi|stamp
  const [stepRes, setStepRes] = useState(null);     // { step, results:[{so_id, ok, message, delivery_id, pending}] }
  const [stepProgress, setStepProgress] = useState("");   // "SO 2 of 3…" live during a multi-order step
  const [history, setHistory] = useState([]);       // recent dispatches [{dispatch_number, deliveries[], at}]

  const loadHistory = React.useCallback(async () => {
    try { const { data } = await api.get("/sap/dispatch-test/history"); setHistory(data.items || []); }
    catch (_) { /* non-blocking */ }
  }, []);
  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  const runStep = async (step) => {
    if (!selectedLines.length) { toast.error("Select at least one line with a ship quantity"); return; }
    if (step === "stamp" && !dispatchId.trim()) { toast.error("Enter a Dispatch Plan ID to stamp"); return; }
    if (step === "gi" && !dispatchId.trim()) { toast.message("No Dispatch ID — shipping only. Enter one to ship + stamp in a single press."); }
    setStepBusy(step);
    const label = step === "prepare" ? "Preparing & releasing" : step === "gi" ? "Posting Goods Issue" : "Stamping Dispatch #";
    const tid = toast.loading(`${label}…`);
    // Run ONE Sales Order per request so each call stays short (a multi-SO call can exceed the gateway /
    // client timeout and look like a silent no-op). Results stream in per SO.
    const so_ids = Array.from(new Set(selectedLines.map((l) => l.so_id)));
    const rowsBySo = {};
    const render = () => setStepRes({ step, results: so_ids.map((sid) => rowsBySo[sid]).filter(Boolean) });
    render();   // switch the table to this step immediately (rows stream in per SO)
    for (let idx = 0; idx < so_ids.length; idx++) {
      const sid = so_ids[idx];
      if (so_ids.length > 1) setStepProgress(`SO ${idx + 1} of ${so_ids.length} · ${sid}…`);
      const lines = selectedLines.filter((l) => l.so_id === sid);
      try {
        const { data } = await api.post("/sap/dispatch-test/step", {
          step, so_ids: [sid], qty_by_line: lines, dispatch_number: dispatchId.trim(),
        }, { timeout: 180000 });
        rowsBySo[sid] = (data.results || [])[0] || { so_id: sid, ok: false, message: "no result" };
      } catch (e) {
        const code = e?.response?.status;
        if (code === 409) {
          rowsBySo[sid] = { so_id: sid, ok: false, message: e?.response?.data?.detail || "busy in another dispatch" };
        } else {
          // Timeout / 502 at the gateway — the work almost certainly completed server-side. Never a hard
          // fail; guide the operator to pull the result (GI persists its legs, so "Stamp (safety)" finishes it).
          rowsBySo[sid] = { so_id: sid, ok: false, pending: true,
            message: step === "gi"
              ? "still finishing in SAP — press '3 · Stamp (safety)' to confirm the delivery #"
              : "taking longer than usual in SAP — press this step again to pull the result" };
        }
      }
      render();
    }
    setStepProgress("");
    const results = so_ids.map((sid) => rowsBySo[sid]).filter(Boolean);
    const okN = results.filter((r) => r.ok).length;
    const pend = results.filter((r) => r.pending).length;
    if (okN === results.length) toast.success(`${label}: all ${okN} OK`, { id: tid });
    else if (pend) toast.message(`${label}: ${pend} still working — press this step again shortly`, { id: tid });
    else toast.error(`${label}: ${okN}/${results.length} OK — see details`, { id: tid });
    if (step === "stamp") loadHistory();
    setStepBusy("");
  };

  const addSo = async () => {
    const q = soInput.trim();
    if (!q) { toast.error("Enter a Sales Order number"); return; }
    if (sos.some((s) => s.so_id === q)) { toast.message(`SO ${q} is already in the plan`); setSoInput(""); return; }
    setAdding(true);
    try {
      const { data } = await api.get("/sap/dispatch-test/load", { params: { so: q }, timeout: 90000 });
      if (!data?.ok) { toast.error(data?.reason || "Sales Order not found"); setAdding(false); return; }
      const lines = (data.lines || []).map((l) => ({ ...l, include: true, ship_qty: l.open_qty }));
      if (!lines.length) { toast.error(`SO ${data.so_id || q} has no open lines to ship`); setAdding(false); return; }
      setSos((prev) => [...prev, { so_id: data.so_id || q, lines }]);
      setSoInput("");
      toast.success(`SO ${data.so_id || q} added (${lines.length} line${lines.length > 1 ? "s" : ""})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
    setAdding(false);
  };

  const removeSo = (soId) => setSos((prev) => prev.filter((s) => s.so_id !== soId));

  const setLine = (soId, i, patch) => setSos((prev) => prev.map((s) => {
    if (s.so_id !== soId) return s;
    const lines = s.lines.slice(); lines[i] = { ...lines[i], ...patch }; return { ...s, lines };
  }));

  const selectedLines = sos.flatMap((s) => s.lines
    .filter((l) => l.include && Number(l.ship_qty) > 0)
    .map((l) => ({ so_id: s.so_id, product: l.product, open_qty: l.open_qty, ship_qty: Number(l.ship_qty) })));

  const overQty = sos.flatMap((s) => s.lines.filter((l) => l.include && Number(l.ship_qty) > Number(l.open_qty)))[0];
  const selectedSoCount = new Set(selectedLines.map((l) => l.so_id)).size;

  const dispatch = async () => {
    if (!selectedLines.length) { toast.error("Select at least one line with a ship quantity"); return; }
    if (overQty) { toast.error(`${overQty.product}: ship qty exceeds open ${overQty.open_qty}`); return; }
    if (!dispatchId.trim()) { toast.error("Enter a Dispatch Plan ID"); return; }
    setBusy(true); setResult(null); setLiveStep("starting dispatch…");
    try {
      const so_ids = sos.filter((s) => selectedLines.some((l) => l.so_id === s.so_id)).map((s) => s.so_id);
      const { data } = await api.post("/sap/dispatch-test/combine", {
        so_ids, qty_by_line: selectedLines, dispatch_number: dispatchId.trim(),
      }, { timeout: 30000 });
      if (!data?.job_id) { toast.error("Could not start the dispatch"); setBusy(false); return; }
      const res = await poll(data.job_id, (d) => {
        const last = (d.steps || []).slice(-1)[0];
        setLiveStep(last ? `${last.step || "working"}${last.so_id ? ` · SO ${last.so_id}` : ""}` : "working…");
      });
      setResult(res);
      const dids = res.deliveries || (res.delivery_id ? [res.delivery_id] : []);
      if (res._clientTimedOut || res.status === "running") {
        toast.message("⏳ Dispatch is still running in SAP — this is taking longer than usual. Do NOT re-dispatch these Sales Orders; refresh in a minute to see the final result.", { duration: 14000 });
      } else if (res.ok && dids.length) {
        toast.success(`${dids.length} outbound deliver${dids.length > 1 ? "ies" : "y"} created`);
      } else if (res.delivery_pending) {
        toast.message("Goods issue posted — delivery numbers materialising in SAP");
      } else if (res.error || res.status === "error") {
        toast.error(res.error || "Dispatch did not complete");
      } else {
        toast.message("Dispatch finished — see the result below");
      }
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 409) {
        // In-flight guard — ALWAYS show it (unique id so repeat attempts are never silently deduped).
        toast.warning(detail || "These Sales Orders are still being dispatched by another job — wait for it to finish.",
          { id: `inflight-${Date.now()}`, duration: 10000 });
        setResult({ status: "blocked", _blocked: true, error: detail });
      } else {
        toast.error(detail || "Dispatch failed");
      }
    }
    setBusy(false); setLiveStep("");
  };

  const dids = result ? (result.deliveries || (result.delivery_id ? [result.delivery_id] : [])) : [];
  const stamps = result?.stamps || [];
  const stampedOk = stamps.filter((s) => s.ok).length;
  const perSo = result?.ship_summary?.per_so || [];
  const idsLagging = !!result?.ship_summary?.ids_lagging;
  const hasError = !!result?.error;
  const steps = result?.steps || result?.results || [];

  return (
    <Layout breadcrumb={["Setup", "Dispatch"]}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 1040 }}>
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 22, fontWeight: 800, margin: 0 }}>
            <Truck size={24} weight="duotone" /> Dispatch Plan
          </h1>
          <p style={{ color: "#64748b", fontSize: 13, margin: "6px 0 0" }}>
            Add one or more Sales Orders, choose the lines and quantities to ship, give the plan a Dispatch ID,
            and dispatch them together. SAP creates one outbound delivery per order — all stamped with your Dispatch ID.
          </p>
        </div>

        {/* 1 — Add Sales Orders */}
        <div style={card}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10 }}>1 &middot; Add Sales Orders to the plan</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input data-testid="dc-so-input" value={soInput} onChange={(e) => setSoInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSo()} placeholder="Sales Order # (e.g. 12823)"
              style={{ ...inp, maxWidth: 260 }} disabled={adding} />
            <button data-testid="dc-add-so-btn" onClick={addSo} disabled={adding}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700,
                padding: "9px 16px", borderRadius: 8, border: "none", background: "#0f172a", color: "#fff",
                cursor: adding ? "default" : "pointer", opacity: adding ? 0.6 : 1 }}>
              <Plus size={15} weight="bold" /> {adding ? "Loading…" : "Add SO"}
            </button>
          </div>
        </div>

        {/* 2 — Builder: per-SO lines */}
        {sos.map((s) => (
          <div key={s.so_id} style={card} data-testid={`dc-so-${s.so_id}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Stack size={18} weight="duotone" color="#4f46e5" />
              <span style={{ fontWeight: 800, fontSize: 14 }}>SO {s.so_id}</span>
              <span style={{ fontSize: 12, color: "#64748b" }}>{s.lines[0]?.site ? `ships from ${s.lines[0].site}` : ""}</span>
              <button data-testid={`dc-remove-${s.so_id}`} onClick={() => removeSo(s.so_id)} title="Remove this SO"
                style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12,
                  fontWeight: 700, padding: "5px 10px", borderRadius: 7, border: "1px solid #FECACA",
                  background: "#FEF2F2", color: "#b91c1c", cursor: "pointer" }}>
                <Trash size={13} weight="bold" /> Remove
              </button>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={{ ...th, width: 36 }}></th>
                <th style={th}>Product</th><th style={th}>Open</th><th style={th}>Stock</th><th style={th}>Ship qty</th>
              </tr></thead>
              <tbody>
                {s.lines.map((l, i) => (
                  <tr key={i} data-testid={`dc-line-${s.so_id}-${l.product}`} style={{ opacity: l.include ? 1 : 0.5 }}>
                    <td style={td}>
                      <input type="checkbox" data-testid={`dc-incl-${s.so_id}-${l.product}`} checked={!!l.include}
                        onChange={(e) => setLine(s.so_id, i, { include: e.target.checked })} />
                    </td>
                    <td style={td}><b>{l.product}</b>{l.description ? <div style={{ fontSize: 11, color: "#94a3b8" }}>{l.description}</div> : null}</td>
                    <td style={td}>{l.open_qty} {l.unit || "EA"}</td>
                    <td style={td}>{l.available != null ? <Chip ok={l.available >= Number(l.ship_qty || 0)}>{l.available} avail</Chip> : "—"}</td>
                    <td style={td}>
                      <input data-testid={`dc-qty-${s.so_id}-${l.product}`} type="number" min="0" max={l.open_qty}
                        value={l.ship_qty} disabled={!l.include}
                        onChange={(e) => setLine(s.so_id, i, { ship_qty: e.target.value })}
                        style={{ ...inp, width: 90, padding: "6px 8px" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {/* 3 — Dispatch */}
        {sos.length > 0 && (
          <div style={card} data-testid="dc-dispatch-panel">
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10 }}>
              2 &middot; Name the plan &amp; dispatch
            </div>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
                Dispatch Plan ID
                <input data-testid="dc-dispatch-id" value={dispatchId} onChange={(e) => setDispatchId(e.target.value)}
                  placeholder="e.g. DISP-WK42" style={{ ...inp, width: 220, marginTop: 4,
                    borderColor: dispatchId.trim() ? "#E2E8F0" : "#f87171" }} />
              </label>
              <button data-testid="dc-dispatch-btn" onClick={dispatch}
                disabled={busy || !selectedLines.length || !dispatchId.trim()}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700,
                  padding: "10px 20px", borderRadius: 8, border: "none",
                  background: (busy || !selectedLines.length || !dispatchId.trim()) ? "#99f6e4" : "#0f766e",
                  color: "#fff", cursor: (busy || !selectedLines.length || !dispatchId.trim()) ? "default" : "pointer" }}>
                <PaperPlaneTilt size={15} weight="bold" />
                {busy ? "Dispatching…" : `Dispatch ${selectedSoCount} SO${selectedSoCount !== 1 ? "s" : ""}`}
              </button>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {selectedLines.length} line{selectedLines.length !== 1 ? "s" : ""} across {selectedSoCount} order{selectedSoCount !== 1 ? "s" : ""}
                {busy && liveStep ? ` · ${liveStep}` : ""}
              </span>
            </div>

            {/* Sequential manual flow — press in order, see each result. No background jobs. */}
            <div style={{ marginTop: 16, borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>Or run it step‑by‑step (recommended)</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                {[["prepare", "1 · Prepare & Release"], ["gi", "2 · Ship + Stamp"], ["stamp", "3 · Stamp (safety)"]].map(([k, lbl]) => (
                  <button key={k} data-testid={`dc-step-${k}`} onClick={() => runStep(k)}
                    disabled={!!stepBusy || !selectedLines.length || (k === "stamp" && !dispatchId.trim())}
                    style={{ fontSize: 13, fontWeight: 700, padding: "9px 16px", borderRadius: 8,
                      border: "1px solid #0f766e", background: stepBusy === k ? "#99f6e4" : "#fff",
                      color: "#0f766e", cursor: (!!stepBusy || !selectedLines.length) ? "default" : "pointer",
                      opacity: (!!stepBusy || !selectedLines.length || (k === "stamp" && !dispatchId.trim())) ? 0.5 : 1 }}>
                    {stepBusy === k ? "Working…" : lbl}
                  </button>
                ))}
                <span style={{ fontSize: 11, color: "#64748b" }}>Press 1 → 2. Step 2 ships AND stamps in one go; use 3 only if a delivery was still materialising.</span>
                {stepBusy && stepProgress && (
                  <span data-testid="dc-step-progress" style={{ fontSize: 11.5, fontWeight: 800, color: "#0f766e",
                    background: "#ccfbf1", border: "1px solid #99f6e4", borderRadius: 999, padding: "3px 10px" }}>
                    {stepProgress}
                  </span>
                )}
              </div>
              {stepRes && (
                <table data-testid="dc-step-results" style={{ width: "100%", borderCollapse: "collapse", marginTop: 10, maxWidth: 720 }}>
                  <thead><tr>
                    <th style={th}>SO</th><th style={th}>Line</th><th style={th}>{stepRes.step}</th><th style={th}>Delivery / note</th>
                  </tr></thead>
                  <tbody>
                    {(stepRes.results || []).flatMap((r) => {
                      const legs = stepRes.step === "gi" ? (r.ship_summary?.legs || []) : (r.legs || []);
                      if (!legs.length) {
                        return [(
                          <tr key={r.so_id} data-testid={`dc-step-row-${r.so_id}`}>
                            <td style={td}><b>{r.so_id}</b></td>
                            <td style={td}>—</td>
                            <td style={td}>{r.ok ? <Chip ok>ok</Chip> : r.pending ? <Chip ok={false}>press this step again</Chip> : <Chip ok={false}>failed</Chip>}</td>
                            <td style={td}>{r.delivery_id ? <b>{r.delivery_id} </b> : ""}{r.message}</td>
                          </tr>
                        )];
                      }
                      return legs.map((lg, j) => {
                        const giStamped = stepRes.step === "gi" ? lg.stamped : null;
                        const chip = lg.ok
                          ? (giStamped === false ? <Chip ok={false}>shipped · stamp pending</Chip> : <Chip ok>{giStamped === true ? "shipped + stamped" : "ok"}</Chip>)
                          : (r.pending ? <Chip ok={false}>press again</Chip> : <Chip ok={false}>failed</Chip>);
                        return (
                          <tr key={`${r.so_id}-${lg.product}-${j}`} data-testid={`dc-step-row-${r.so_id}-${j}`}>
                            <td style={td}>{j === 0 ? <b>{r.so_id}</b> : ""}</td>
                            <td style={td}>{lg.product}{lg.ship_qty != null ? ` ×${lg.ship_qty}` : (lg.shipped != null ? ` ×${lg.shipped}` : "")}</td>
                            <td style={td}>{chip}</td>
                            <td style={td}>{lg.delivery_id ? <b>{lg.delivery_id}</b> : (lg.message || "")}</td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Recent dispatches — quick lookup of past Dispatch IDs → delivery numbers */}
        {history.length > 0 && (
          <div style={card} data-testid="dc-history">
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Clock size={15} /> Recent dispatches
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", maxWidth: 720 }}>
              <thead><tr>
                <th style={th}>Dispatch ID</th><th style={th}>Orders</th><th style={th}>Deliveries</th>
              </tr></thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} data-testid={`dc-history-row-${h.dispatch_number}`}>
                    <td style={td}><b>{h.dispatch_number}</b></td>
                    <td style={td}>{(h.so_ids || []).join(", ")}</td>
                    <td style={td}>{(h.deliveries || []).length ? h.deliveries.join(", ") : <span style={{ color: "#94a3b8" }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 4 — Result */}
        {result && (
          <div style={card} data-testid="dc-result">
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 14, marginBottom: 10 }}>
              {dids.length ? <CheckCircle size={18} weight="fill" color="#16a34a" /> : <Package size={18} weight="fill" color="#d97706" />}
              3 &middot; SAP result
              <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Clock size={13} /> {result.elapsed_seconds != null ? `${result.elapsed_seconds}s` : ""}
              </span>
            </div>
            <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.8 }}>
              {(result.status === "running" || result._clientTimedOut) && (
                <div data-testid="dc-still-running" style={{ color: "#92400e", fontWeight: 700, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
                  ⏳ Still processing in SAP — this dispatch has not finished yet and goods may already be issuing.
                  Do NOT re-dispatch these Sales Orders (you would double-ship the customer). Refresh in a minute to see the final result.
                </div>
              )}
              {result._blocked && (
                <div data-testid="dc-blocked" style={{ color: "#92400e", fontWeight: 700, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px" }}>
                  {result.error || "These Sales Orders are already being dispatched by another job. Wait for it to finish — do NOT re-dispatch."}
                </div>
              )}
              {dids.length > 0 && (
                <div data-testid="dc-deliveries">
                  Outbound deliver{dids.length > 1 ? "ies" : "y"} created:&nbsp;
                  <b data-testid="dc-delivery-id">{dids.join(", ")}</b> &nbsp;<Chip ok>Goods issue posted</Chip>
                </div>
              )}
              {dids.length > 0 && (
                <div data-testid="dc-stamp-line">
                  Dispatch Plan ID <b>{result.dispatch_number || dispatchId}</b> &nbsp;
                  {stamps.length
                    ? (stampedOk === stamps.length
                        ? <Chip ok>stamped on all {stamps.length} ✓</Chip>
                        : <Chip ok={false}>stamped {stampedOk}/{stamps.length}</Chip>)
                    : <Chip ok={false}>not stamped</Chip>}
                </div>
              )}
              {idsLagging && (
                <div data-testid="dc-pending-line" style={{ color: "#92400e" }}>
                  Goods issue posted for every line — some delivery #s are still replicating in SAP and will be stamped automatically once they appear. Re-add the SO in a minute to confirm its lines are at 0 open.
                </div>
              )}
              {hasError && (
                <div data-testid="dc-error-line" style={{ color: "#b91c1c", fontWeight: 600 }}>
                  {result.error || "Some lines did not ship."}
                </div>
              )}
              {perSo.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10, maxWidth: 680 }}>
                  <thead><tr>
                    <th style={th}>SO</th><th style={th}>Deliveries</th><th style={th}>Lines shipped</th><th style={th}>Status</th>
                  </tr></thead>
                  <tbody>
                    {perSo.map((p, i) => (
                      <tr key={i} data-testid={`dc-per-so-${p.so_id}`}>
                        <td style={td}><b>{p.so_id}</b></td>
                        <td style={td}>{(p.deliveries || []).join(", ") || "—"}</td>
                        <td style={td}>{(p.lines || []).map((l) => `${l.product}×${l.shipped ?? l.requested}${l.open > 0 ? ` (open ${l.open})` : ""}`).join(", ") || "—"}</td>
                        <td style={td}>
                          {p.short ? <Chip ok={false}>short — not shipped</Chip>
                            : p.ids_lagging ? <Chip ok>complete · id materialising</Chip>
                            : <Chip ok>complete</Chip>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {steps.length > 0 && (hasError || !dids.length) && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: "pointer", fontSize: 12, color: "#64748b" }}>Step trace ({steps.length})</summary>
                  <pre style={{ fontSize: 11, background: "#f8fafc", padding: 8, borderRadius: 6, overflowX: "auto", maxHeight: 220 }}>
                    {steps.map((s) => JSON.stringify(s)).join("\n")}
                  </pre>
                </details>
              )}
              {!dids.length && !idsLagging && !hasError && result.status !== "running"
                && !result._clientTimedOut && !result._blocked && (
                <div style={{ color: "#64748b" }}>No deliveries were created.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
